import { PreRegistrationAttendee, PreRegistrationGroup } from "@/types";
import client, { throwCaughtError } from "../client";
import { UseSuspenseQueryResult } from "@tanstack/react-query";
import { useBuildingSuspenseQuery } from "@/components/RouterSetup";

const PersonalPreregistrationGroupQueryFn = async (
  hostId: string,
  timespan = "today" as "today" | "future",
) => {
  try {
    const response = await client.get(
      `/getPersonalPreRegistrations?userId=${hostId}&timespan=${timespan}`,
    );
    return response.data.groups;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const personPreregistrationOptions = (
  hostId: string,
  timespan = "today" as "today" | "future",
) => ({
  queryKey: ["personal-preregistration-group", hostId, timespan],
  queryFn: async () =>
    await PersonalPreregistrationGroupQueryFn(hostId, timespan),
});

export const companyPreregistrationOptions = (
  companyId: string,
  timespan = "today" as "today" | "future",
) => ({
  queryKey: ["company-preregistration-group", companyId, timespan],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getCompanyPreregistrations?companyId=${companyId}&timespan=${timespan}`,
      );
      return response.data.groups;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useCompanyPreregistrations = (
  companyId: string,
  timespan = "today" as "today" | "future",
): UseSuspenseQueryResult<PreRegistrationGroup[]> => {
  return useBuildingSuspenseQuery(
    companyPreregistrationOptions(companyId, timespan),
  );
};

export const buildingPreregistrationsOptions = (
  timespan = "today" as "today" | "future",
  refetchInterval = 2500,
) => ({
  queryKey: ["building-preregistration-group", timespan],
  refetchInterval,
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getBuildingPreRegistrations?timespan=${timespan}`,
      );
      return response.data.groups;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const createPreregistrationGroup = async (
  hostId: string,
  eventName: string,
  eventDescription: string,
  startTime: Date,
  notifyOnEveryArrival: boolean,
  attendees: PreRegistrationAttendee[],
) => {
  return await client.post("/createPreRegistration", {
    hostId,
    eventName,
    eventDescription,
    startTime: startTime.toISOString(),
    notifyOnEveryArrival,
    attendees,
  });
};

export const deletePreregistrationGroup = async (
  groupId: string,
  userId: string,
) => {
  return await client.delete(
    `/deletePreRegistration?groupId=${groupId}&userId=${userId}`,
  );
};

export const updatePreregistrationGroup = async (
  userId: string,
  groupId: string,
  updateData: Partial<PreRegistrationGroup>,
) => {
  return await client.patch("/updatePreRegistration", {
    userId,
    groupId,
    updateData,
  });
};
